import { npubEncode } from '@nostr/tools/nip19'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import { mutePubkeySetAtom } from '@/atoms/lists'
import { mutePrivately, mutePublicly, unmute } from '@/atoms/mutes'
import { canWriteAtom, unwrappedSignerAtom, currentPubkeyAtom } from '@/atoms/session'
import { HugeiconsIcon } from '@hugeicons/react'
import { BellIcon, BellOffIcon, Copy01Icon, EllipsisIcon } from '@hugeicons/core-free-icons'
import { useAtomValue } from 'jotai'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import SaveToFollowSetItems from './SaveToFollowSetItems'

export default function ProfileOptions({ pubkey }: { pubkey: string }) {
  const { t } = useTranslation()
  const signer = useAtomValue(unwrappedSignerAtom) ?? null
  const canWrite = useAtomValue(canWriteAtom)
  const accountPubkey = useAtomValue(currentPubkeyAtom)
  const mutePubkeySet = useAtomValue(mutePubkeySetAtom)
  const isMuted = useMemo(() => mutePubkeySet.has(pubkey), [mutePubkeySet, pubkey])

  if (pubkey === accountPubkey) return null

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="secondary" size="icon" className="rounded-full">
          <HugeiconsIcon icon={EllipsisIcon} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuItem onClick={() => navigator.clipboard.writeText(npubEncode(pubkey))}>
          <HugeiconsIcon icon={Copy01Icon} />
          {t('Copy user ID')}
        </DropdownMenuItem>
        {canWrite &&
          (isMuted ? (
            <DropdownMenuItem
              onClick={() => unmute(pubkey)}
              className="text-destructive focus:text-destructive"
            >
              <HugeiconsIcon icon={BellIcon} />
              {t('Unmute user')}
            </DropdownMenuItem>
          ) : (
            <>
              <DropdownMenuItem
                onClick={() => {
                  if (signer?.nip04Decrypt) mutePrivately(pubkey)
                }}
                className="text-destructive focus:text-destructive"
                disabled={!signer?.nip04Decrypt}
                title={
                  !signer?.nip04Decrypt
                    ? t('Your login method does not support encryption')
                    : undefined
                }
              >
                <HugeiconsIcon icon={BellOffIcon} />
                {t('Mute user privately')}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => mutePublicly(pubkey)}
                className="text-destructive focus:text-destructive"
              >
                <HugeiconsIcon icon={BellOffIcon} />
                {t('Mute user publicly')}
              </DropdownMenuItem>
            </>
          ))}
        <DropdownMenuSeparator />
        <SaveToFollowSetItems pubkey={pubkey} />
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
